import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Phone, Users, Video } from "lucide-react";
import { api, type Meeting } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button, Input, Label } from "../components/ui";

export function DashboardPage() {
  const { user, token, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<"DIRECT" | "CONFERENCE">("CONFERENCE");
  const [e2eeEnabled, setE2eeEnabled] = useState(false);
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .listMeetings(token)
      .then(({ meetings: m }) => setMeetings(m))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"));
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-400">
        Loading…
      </div>
    );
  }
  if (!user || !token) return <Navigate to="/login" replace />;

  async function startMeeting() {
    if (!title.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const { meeting, guestInviteToken } = await api.createMeeting(token, {
        title: title.trim(),
        type,
        e2eeEnabled,
        recordingEnabled,
      });
      sessionStorage.setItem(`invite_${meeting.id}`, guestInviteToken);
      navigate(`/room/${meeting.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create meeting");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-full max-w-6xl flex-col gap-8 p-6 md:p-10">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent-500/20 text-accent-400 ring-1 ring-accent-500/30">
            <Video className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-ink-500">BridgeCall</p>
            <h1 className="font-display text-2xl font-bold">Welcome, {user.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-white/5 px-3 py-1 text-xs ring-1 ring-white/10">
            {user.role}
          </span>
          <Button type="button" variant="ghost" onClick={logout}>
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <motion.section
          className="glass-panel rounded-3xl p-6"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="font-display mb-1 text-lg font-semibold">Start a session</h2>
          <p className="mb-5 text-sm text-ink-500">
            Direct calls or conferences — invite-only access for every room.
          </p>

          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                placeholder="Product sync, client call…"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <Label>Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setType("DIRECT")}
                  className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                    type === "DIRECT"
                      ? "border-accent-500/50 bg-accent-500/10"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <Phone className="mb-2 h-4 w-4 text-accent-400" />
                  Direct call
                </button>
                <button
                  type="button"
                  onClick={() => setType("CONFERENCE")}
                  className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                    type === "CONFERENCE"
                      ? "border-accent-500/50 bg-accent-500/10"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <Users className="mb-2 h-4 w-4 text-accent-400" />
                  Conference
                </button>
              </div>
            </div>
            <div className="space-y-2 rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={e2eeEnabled}
                  onChange={(e) => setE2eeEnabled(e.target.checked)}
                />
                End-to-end encryption (chat + optional media insertable streams)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={recordingEnabled}
                  onChange={(e) => setRecordingEnabled(e.target.checked)}
                />
                Allow meeting recording upload
              </label>
            </div>
            {error && <p className="text-sm text-red-300">{error}</p>}
            <Button type="button" className="w-full" onClick={startMeeting} disabled={creating}>
              {creating ? "Creating…" : "Create & join"}
            </Button>
          </div>
        </motion.section>

        <motion.section
          className="glass-panel rounded-3xl p-6"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <h2 className="font-display mb-4 text-lg font-semibold">Your meetings</h2>
          <ul className="space-y-2">
            {meetings.length === 0 && (
              <li className="rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-ink-500">
                No meetings yet — create your first room above.
              </li>
            )}
            {meetings.map((m) => (
              <li
                key={m.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white/5 px-4 py-3 ring-1 ring-white/5"
              >
                <div>
                  <p className="font-medium">{m.title}</p>
                  <p className="text-xs text-ink-500">
                    {m.type} · {m.status} · {m.host?.name ?? "Host"}
                  </p>
                </div>
                {m.status !== "ENDED" ? (
                  <Link to={`/room/${m.id}`}>
                    <Button type="button" variant="ghost">
                      Join
                    </Button>
                  </Link>
                ) : (
                  <span className="text-xs text-ink-500">Ended</span>
                )}
              </li>
            ))}
          </ul>
        </motion.section>
      </div>
    </div>
  );
}
